import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2, Pencil, Bell, Send, History, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { calendarNotificationsService } from '../services';

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EMPTY_FORM = {
  name: '',
  is_active: true,
  frequency: 'daily',
  send_hour: 7,
  send_minute: 0,
  day_of_week: 0,
  day_of_month: 1,
  recipient_mode: 'all_internal',
  recipient_user_ids: [],
  job_scope: 'all_jobs',
  timezone: 'Europe/London',
};

const pad2 = (n) => String(n).padStart(2, '0');

export default function CalendarNotificationsDialog({ open, onOpenChange }) {
  const { toast } = useToast();
  const [rules, setRules] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [timezones, setTimezones] = useState(['Europe/London']);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sendingId, setSendingId] = useState(null);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      calendarNotificationsService.list(),
      calendarNotificationsService.getRecipients(),
      calendarNotificationsService.getTimezones(),
    ])
      .then(([rulesData, recipientsData, tzData]) => {
        setRules(rulesData || []);
        setRecipients(recipientsData || []);
        setTimezones(tzData?.timezones || ['Europe/London']);
      })
      .catch((err) => toast({ title: 'Could not load notification rules', description: err?.response?.data?.detail || err.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    if (!open) return;
    loadAll();
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setLogsFor(null);
  }, [open, loadAll]);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name,
      is_active: rule.is_active,
      frequency: rule.frequency,
      send_hour: rule.send_hour,
      send_minute: rule.send_minute,
      day_of_week: rule.day_of_week ?? 0,
      day_of_month: rule.day_of_month ?? 1,
      recipient_mode: rule.recipient_mode,
      recipient_user_ids: rule.recipient_user_ids || [],
      job_scope: rule.job_scope,
      timezone: rule.timezone || 'Europe/London',
    });
    setShowForm(true);
  };

  const toggleRecipient = (userId) => {
    setForm((f) => {
      const set = new Set(f.recipient_user_ids);
      if (set.has(userId)) set.delete(userId); else set.add(userId);
      return { ...f, recipient_user_ids: Array.from(set) };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    if (form.recipient_mode === 'individual' && form.recipient_user_ids.length === 0) {
      toast({ title: 'Select at least one recipient', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        is_active: form.is_active,
        frequency: form.frequency,
        send_hour: Number(form.send_hour),
        send_minute: Number(form.send_minute),
        day_of_week: ['weekly', 'fortnightly'].includes(form.frequency) ? Number(form.day_of_week) : null,
        day_of_month: form.frequency === 'monthly' ? Number(form.day_of_month) : null,
        recipient_mode: form.recipient_mode,
        recipient_user_ids: form.recipient_mode === 'individual' ? form.recipient_user_ids : null,
        job_scope: form.job_scope,
        timezone: form.timezone,
      };
      if (editingId) {
        await calendarNotificationsService.update(editingId, payload);
        toast({ title: 'Notification rule updated' });
      } else {
        await calendarNotificationsService.create(payload);
        toast({ title: 'Notification rule created' });
      }
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await calendarNotificationsService.remove(deleteTarget.id);
      toast({ title: 'Notification rule deleted' });
      setDeleteTarget(null);
      loadAll();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleSendNow = async (rule) => {
    setSendingId(rule.id);
    try {
      const result = await calendarNotificationsService.sendNow(rule.id);
      if (result.recipients_sent > 0) {
        toast({ title: 'Digest sent', description: `${result.jobs_count} job(s) summarised, sent to ${result.recipients_sent} recipient(s).` });
      } else {
        toast({
          title: 'Digest generated but not delivered',
          description: result.errors?.[0] || 'No emails were sent (check recipients / email configuration).',
          variant: 'destructive',
        });
      }
      loadAll();
    } catch (err) {
      toast({ title: 'Send failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSendingId(null);
    }
  };

  const openLogs = async (rule) => {
    setLogsFor(rule);
    try {
      const data = await calendarNotificationsService.getLogs(rule.id);
      setLogs(data || []);
    } catch (err) {
      toast({ title: 'Could not load history', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const describeSchedule = (rule) => {
    const time = `${pad2(rule.send_hour)}:${pad2(rule.send_minute)}`;
    const tzSuffix = rule.timezone || 'Europe/London';
    if (rule.frequency === 'daily') return `Daily at ${time} (${tzSuffix})`;
    if (rule.frequency === 'weekly') return `Weekly, every ${WEEKDAYS[rule.day_of_week ?? 0]} at ${time} (${tzSuffix})`;
    if (rule.frequency === 'fortnightly') return `Fortnightly, every other ${WEEKDAYS[rule.day_of_week ?? 0]} at ${time} (${tzSuffix})`;
    if (rule.frequency === 'monthly') return `Monthly on day ${rule.day_of_month} at ${time} (${tzSuffix})`;
    return rule.frequency;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="calendar-notifications-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Job Notification Digests
            </DialogTitle>
            <DialogDescription>
              Automated email summaries of scheduled jobs, sent on a recurring schedule to auditors or internal staff.
            </DialogDescription>
          </DialogHeader>

          {!showForm ? (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button size="sm" onClick={startCreate} data-testid="add-notification-rule-btn">
                  <Plus className="h-4 w-4 mr-1" /> New Rule
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : rules.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-10" data-testid="no-notification-rules">
                  No notification rules yet. Click "New Rule" to set up an automated digest.
                </div>
              ) : (
                <div className="space-y-3">
                  {rules.map((rule) => (
                    <div key={rule.id} className="border rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`notification-rule-${rule.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{rule.name}</span>
                          <Badge variant={rule.is_active ? 'default' : 'secondary'}>{rule.is_active ? 'Active' : 'Paused'}</Badge>
                          <Badge variant="outline">{rule.recipient_mode === 'all_internal' ? 'All internal users' : `${rule.recipient_user_ids.length} recipient(s)`}</Badge>
                          <Badge variant="outline">{rule.job_scope === 'all_jobs' ? 'All jobs' : 'Assigned jobs only'}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{describeSchedule(rule)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {rule.last_sent_at ? `Last sent: ${new Date(rule.last_sent_at).toLocaleString()}` : 'Never sent yet'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => handleSendNow(rule)} disabled={sendingId === rule.id} title="Send now" data-testid={`send-now-rule-${rule.id}`}>
                          {sendingId === rule.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openLogs(rule)} title="History" data-testid={`history-rule-${rule.id}`}>
                          <History className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => startEdit(rule)} title="Edit" data-testid={`edit-rule-${rule.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(rule)} title="Delete" data-testid={`delete-rule-${rule.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {logsFor && (
                <div className="border rounded-lg p-3 bg-muted/30" data-testid="notification-rule-logs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">History — {logsFor.name}</span>
                    <Button variant="ghost" size="icon" onClick={() => setLogsFor(null)}><X className="h-4 w-4" /></Button>
                  </div>
                  {logs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No sends yet.</p>
                  ) : (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {logs.map((log) => (
                        <div key={log.id} className="text-xs flex items-center justify-between border-b last:border-0 py-1">
                          <span>{new Date(log.sent_at).toLocaleString()} {log.triggered_manually && '(manual)'}</span>
                          <span>{log.jobs_count} jobs · {log.recipients_sent} sent{log.recipients_failed ? `, ${log.recipients_failed} failed` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Rule Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Daily 7am Job Digest"
                  data-testid="rule-name-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="rule-active-switch" />
                <Label>Active</Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frequency</Label>
                  <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                    <SelectTrigger data-testid="rule-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Send Time</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number" min={0} max={23} value={form.send_hour}
                      onChange={(e) => setForm((f) => ({ ...f, send_hour: e.target.value }))}
                      className="w-20" data-testid="rule-send-hour-input"
                    />
                    <span>:</span>
                    <Input
                      type="number" min={0} max={59} value={form.send_minute}
                      onChange={(e) => setForm((f) => ({ ...f, send_minute: e.target.value }))}
                      className="w-20" data-testid="rule-send-minute-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Timezone</Label>
                <Select value={form.timezone} onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}>
                  <SelectTrigger data-testid="rule-timezone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Send time auto-adjusts for daylight saving in this zone (e.g. Europe/London switches BST/GMT automatically).
                </p>
              </div>

              {['weekly', 'fortnightly'].includes(form.frequency) && (
                <div>
                  <Label>Day of Week</Label>
                  <Select value={String(form.day_of_week)} onValueChange={(v) => setForm((f) => ({ ...f, day_of_week: Number(v) }))}>
                    <SelectTrigger data-testid="rule-day-of-week-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d, idx) => <SelectItem key={idx} value={String(idx)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.frequency === 'monthly' && (
                <div>
                  <Label>Day of Month</Label>
                  <Input
                    type="number" min={1} max={31} value={form.day_of_month}
                    onChange={(e) => setForm((f) => ({ ...f, day_of_month: e.target.value }))}
                    className="w-24" data-testid="rule-day-of-month-input"
                  />
                </div>
              )}

              <div>
                <Label>Recipients</Label>
                <Select value={form.recipient_mode} onValueChange={(v) => setForm((f) => ({ ...f, recipient_mode: v }))}>
                  <SelectTrigger data-testid="rule-recipient-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_internal">All internal users (auditors & staff)</SelectItem>
                    <SelectItem value="individual">Selected individuals</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.recipient_mode === 'individual' && (
                <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1" data-testid="rule-recipient-checklist">
                  {recipients.map((r) => (
                    <div key={r.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={form.recipient_user_ids.includes(r.id)}
                        onCheckedChange={() => toggleRecipient(r.id)}
                        data-testid={`recipient-checkbox-${r.id}`}
                      />
                      <span className="text-sm">{r.name} <span className="text-muted-foreground">({r.roles.join(', ')})</span></span>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <Label>Jobs to Summarise</Label>
                <Select value={form.job_scope} onValueChange={(v) => setForm((f) => ({ ...f, job_scope: v }))}>
                  <SelectTrigger data-testid="rule-job-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_jobs">All scheduled jobs in the interval</SelectItem>
                    <SelectItem value="assigned_only">Only jobs assigned to each recipient</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-rule-form-btn">Cancel</Button>
                <Button onClick={handleSave} disabled={busy} data-testid="save-rule-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  {editingId ? 'Save Changes' : 'Create Rule'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete notification rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will stop "{deleteTarget?.name}" from sending any further digests. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-rule-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={busy} data-testid="confirm-delete-rule-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
